/* Nidraka by Cosylabs — product line behaviour.
   Self-contained: no dependency on Dawn's variant-selects or section rendering.
   Add-to-cart posts to the theme's cart drawer if present, else /cart. */

(function () {
  'use strict';

  /* ---------------------------------------------------------------- product */

  class NidrakaProduct extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('[data-nk-variants]');
      if (!data) return;

      try {
        this.variants = JSON.parse(data.textContent);
      } catch (e) {
        return;
      }

      this.idInput = this.querySelector('[data-nk-variant-id]');
      this.priceEl = this.querySelector('[data-nk-price]');
      this.compareEl = this.querySelector('[data-nk-compare]');
      this.saveEl = this.querySelector('[data-nk-save]');
      this.atc = this.querySelector('[data-nk-atc]');
      this.form = this.querySelector('form');
      this.errorEl = this.querySelector('[data-nk-error]');
      this.inputs = Array.from(this.querySelectorAll('[data-nk-opt]'));

      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-nk-opt]')) this.update();
      });

      if (this.form) {
        this.form.addEventListener('submit', (e) => this.submit(e));
      }

      /* Size Guide link: let the browser do the scroll, and tell the size
         guide which row to open. One-way only — the size guide never changes
         the variant. */
      const sizeLink = this.querySelector('[data-nk-sizeguide]');
      if (sizeLink) {
        sizeLink.addEventListener('click', () => {
          document.dispatchEvent(
            new CustomEvent('nidraka:size-guide', {
              detail: { values: this.selection().filter(Boolean) }
            })
          );
        });
      }

      this.update({ silent: true });
    }

    selection() {
      const sel = [];
      this.inputs.forEach((el) => {
        const i = Number(el.dataset.nkOptIndex);
        if (el.tagName === 'SELECT') sel[i] = el.value;
        else if (el.checked) sel[i] = el.value;
      });
      return sel;
    }

    match(selection) {
      return this.variants.find((v) =>
        selection.every((val, i) => val === undefined || v.options[i] === val)
      );
    }

    update(opts) {
      const selection = this.selection();
      const variant = this.match(selection);

      this.refreshAvailability(selection);

      if (this.idInput) this.idInput.value = variant ? variant.id : '';

      if (variant && this.priceEl) this.priceEl.innerHTML = variant.price;

      if (this.compareEl) {
        const showCompare = variant && variant.compare_at_price && variant.compare_at_price !== variant.price;
        this.compareEl.innerHTML = showCompare ? variant.compare_at_price : '';
        this.compareEl.hidden = !showCompare;
        if (this.saveEl) {
          this.saveEl.hidden = !showCompare;
          if (showCompare && variant.save_pct) this.saveEl.textContent = 'Save ' + variant.save_pct + '%';
        }
      }

      if (this.atc) {
        const ok = variant && variant.available;
        this.atc.disabled = !ok;
        this.atc.textContent = !variant
          ? 'Unavailable'
          : variant.available
          ? this.atc.dataset.nkAtc || 'Add to Cart'
          : 'Sold out';
      }

      if (variant && variant.media_index !== null && variant.media_index !== undefined) {
        const gallery = this.querySelector('nidraka-gallery');
        if (gallery && gallery.show) gallery.show(variant.media_index);
      }

      if (variant && !(opts && opts.silent) && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    /* Grey out option values that produce no purchasable variant against the
       rest of the current selection. Price is not shown per option — it depends
       on the whole combination, so it lives once at the top of the buy box. */
    refreshAvailability(selection) {
      this.querySelectorAll('select[data-nk-opt]').forEach((select) => {
        const index = Number(select.dataset.nkOptIndex);

        Array.from(select.options).forEach((option) => {
          const probe = selection.slice();
          probe[index] = option.value;

          const variant = this.match(probe);
          /* never disable what is currently chosen, or the shopper gets
             stranded on an option they cannot move away from */
          option.disabled = option.selected ? false : !variant || !variant.available;
        });
      });
    }

    async submit(event) {
      if (!this.atc || this.atc.disabled) return;
      event.preventDefault();

      /* Which cart owns the drawer on this store? Shopflo replaces the theme's
         own drawer and exposes handleFloCartBtn() from its bundle; the theme's
         product-form.js calls that instead of cart.renderContents(), which is
         commented out there. Match that, or Shopflo's drawer never opens. */
      const flo = typeof window.handleFloCartBtn === 'function';
      const drawer = flo
        ? null
        : document.querySelector('cart-drawer') || document.querySelector('cart-notification');

      this.atc.setAttribute('aria-busy', 'true');
      if (this.errorEl) this.errorEl.hidden = true;

      const body = new FormData(this.form);
      if (drawer && typeof drawer.getSectionsToRender === 'function') {
        body.append(
          'sections',
          drawer.getSectionsToRender().map((s) => s.id).join(',')
        );
        body.append('sections_url', window.location.pathname);
      }

      try {
        const res = await fetch((window.routes && window.routes.cart_add_url) || '/cart/add', {
          method: 'POST',
          headers: { Accept: 'application/javascript' },
          body: body
        });
        const json = await res.json();

        if (json.status) {
          this.showError(json.description || json.message);
          return;
        }

        /* keep the header count and anything else subscribed in sync */
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: 'nidraka-product',
            productVariantId: body.get('id'),
            cartData: json
          });
        }

        if (flo) {
          window.handleFloCartBtn();
        } else if (drawer) {
          if (typeof drawer.renderContents === 'function') drawer.renderContents(json);
          if (drawer.classList) drawer.classList.remove('is-empty');
        } else {
          /* no drawer of any kind — send them to the cart page */
          window.location = (window.routes && window.routes.cart_url) || '/cart';
        }

        document.dispatchEvent(new CustomEvent('nidraka:added', { detail: json }));
      } catch (e) {
        this.showError('Something went wrong. Please try again.');
      } finally {
        this.atc.removeAttribute('aria-busy');
      }
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }
  }

  /* ---------------------------------------------------------------- gallery */

  class NidrakaGallery extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-nk-media]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-nk-thumb]'));

      this.thumbs.forEach((thumb, i) => {
        thumb.addEventListener('click', () => this.show(i));
      });
    }

    show(index) {
      if (!this.items.length || index < 0 || index >= this.items.length) return;

      this.items.forEach((item, i) => {
        const on = i === index;
        item.classList.toggle('is-active', on);
        // Leaving a video: stop it, or it keeps playing behind the next item.
        // Shopify's own player is an iframe, so its src has to be cut instead.
        if (!on) {
          const video = item.querySelector('video');
          if (video && !video.paused) video.pause();

          const frame = item.querySelector('iframe');
          if (frame && frame.src) {
            frame.dataset.nkSrc = frame.dataset.nkSrc || frame.src;
            frame.removeAttribute('src');
          }
        } else {
          const frame = item.querySelector('iframe');
          if (frame && !frame.getAttribute('src') && frame.dataset.nkSrc) {
            frame.src = frame.dataset.nkSrc;
          }
        }
      });

      this.thumbs.forEach((t, i) => t.setAttribute('aria-current', i === index ? 'true' : 'false'));
    }
  }

  /* ----------------------------------------------------------------- layers */

  class NidrakaLayers extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-nk-layer]'));
      this.images = Array.from(this.querySelectorAll('[data-nk-layer-img]'));
      this.chips = Array.from(this.querySelectorAll('[data-nk-layer-chip]'));

      this.items.forEach((item, i) => {
        item.addEventListener('toggle', () => {
          if (item.open) this.activate(i);
        });
      });

      this.chips.forEach((chip, i) => {
        chip.addEventListener('click', () => {
          if (this.items[i]) this.items[i].open = true;
          this.activate(i);
        });
      });

      const openIndex = this.items.findIndex((item) => item.open);
      this.activate(openIndex === -1 ? 0 : openIndex);
    }

    activate(index) {
      this.images.forEach((img, i) => img.setAttribute('data-active', i === index ? 'true' : 'false'));
      this.chips.forEach((chip, i) => chip.setAttribute('aria-current', i === index ? 'true' : 'false'));
    }
  }

  /* ------------------------------------------------------------- size guide */

  class NidrakaSizeGuide extends HTMLElement {
    connectedCallback() {
      this.rows = Array.from(this.querySelectorAll('[data-nk-size-row]'));
      this.name = this.querySelector('[data-nk-size-name]');
      this.dimW = this.querySelector('[data-nk-dim-w]');
      this.dimL = this.querySelector('[data-nk-dim-l]');
      this.dimH = this.querySelector('[data-nk-dim-h]');
      this.images = Array.from(this.querySelectorAll('[data-nk-size-img]'));

      this.rows.forEach((row, i) => {
        row.addEventListener('click', () => this.select(i));
        row.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.select(i);
          }
        });
      });

      const start = this.rows.findIndex((r) => r.dataset.nkDefault === 'true');
      this.select(start === -1 ? 0 : start);

      this.onRequest = (event) => this.selectByValues(event.detail && event.detail.values);
      document.addEventListener('nidraka:size-guide', this.onRequest);
    }

    disconnectedCallback() {
      if (this.onRequest) document.removeEventListener('nidraka:size-guide', this.onRequest);
    }

    /* Opens the row matching the variant options currently chosen in the buy
       box. An explicit match rule wins; otherwise fall back to the size name,
       preferring the longest one so "Single XL" beats "Single". */
    selectByValues(values) {
      if (!values || !values.length) return;

      const norm = (v) => String(v || '').toLowerCase().trim();
      const selected = values.map(norm).filter(Boolean);
      if (!selected.length) return;

      let explicitHit = -1;
      let nameHit = -1;
      let nameLength = 0;

      this.rows.forEach((row, i) => {
        const tokens = (row.dataset.nkMatch || '').split(',').map(norm).filter(Boolean);

        if (tokens.length) {
          /* exact, not substring: "72 Inch" must not match inside
             "King | 72 Inch" */
          const all = tokens.every((token) => selected.indexOf(token) !== -1);
          if (all && explicitHit === -1) explicitHit = i;
          return;
        }

        const name = norm(row.dataset.nkName);
        if (name && name.length > nameLength && selected.some((value) => value.indexOf(name) !== -1)) {
          nameHit = i;
          nameLength = name.length;
        }
      });

      const target = explicitHit !== -1 ? explicitHit : nameHit;
      if (target !== -1) this.select(target);
    }

    select(index) {
      const row = this.rows[index];
      if (!row) return;

      this.rows.forEach((r, i) => r.setAttribute('aria-current', i === index ? 'true' : 'false'));

      if (this.name) this.name.textContent = row.dataset.nkName || '';
      if (this.dimW) this.dimW.textContent = row.dataset.nkW || '';
      if (this.dimL) this.dimL.textContent = row.dataset.nkL || '';
      if (this.dimH) this.dimH.textContent = row.dataset.nkH ? 'Height: ' + row.dataset.nkH : '';

      this.images.forEach((img, i) => img.setAttribute('data-active', i === index ? 'true' : 'false'));
    }
  }

  /* -------------------------------------------------------------- carousel */

  class NidrakaCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-nk-track]');
      if (!this.track) return;

      this.prevBtn = this.querySelector('[data-nk-prev]');
      this.nextBtn = this.querySelector('[data-nk-next]');
      this.counter = this.querySelector('[data-nk-count]');

      /* deliberately not named scrollBy — Element already has that method */
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.advance(-1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.advance(1));

      this.track.addEventListener('scroll', () => this.sync(), { passive: true });
      window.addEventListener('resize', () => this.sync(), { passive: true });
      this.sync();
    }

    step() {
      const first = this.track.firstElementChild;
      if (!first) return 320;
      const styles = getComputedStyle(this.track);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      return first.getBoundingClientRect().width + gap;
    }

    advance(direction) {
      this.track.scrollBy({ left: direction * this.step(), behavior: 'smooth' });
    }

    sync() {
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      if (this.prevBtn) this.prevBtn.disabled = this.track.scrollLeft <= 2;
      if (this.nextBtn) this.nextBtn.disabled = this.track.scrollLeft >= max;

      if (this.counter) {
        const total = this.track.children.length;
        const index = Math.min(total, Math.round(this.track.scrollLeft / this.step()) + 1);
        this.counter.textContent = index + ' / ' + total;
      }
    }
  }

  /* ----------------------------------------------------------------- modal */

  /* Native <dialog>: gets focus trapping, Esc to close and the backdrop for
     free, so there is nothing to hand-roll. */
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-nk-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.nkOpen);
      if (dialog && typeof dialog.showModal === 'function') {
        event.preventDefault();
        dialog.showModal();
      }
      return;
    }

    const closer = event.target.closest('[data-nk-close]');
    if (closer) {
      const dialog = closer.closest('dialog');
      if (dialog) dialog.close();
      return;
    }

    /* click on the backdrop closes it */
    if (event.target.matches('dialog[data-nk-modal]')) event.target.close();
  });

  /* ---------------------------------------------------------------- define */

  const register = (name, klass) => {
    if (!customElements.get(name)) customElements.define(name, klass);
  };

  register('nidraka-product', NidrakaProduct);
  register('nidraka-gallery', NidrakaGallery);
  register('nidraka-layers', NidrakaLayers);
  register('nidraka-size-guide', NidrakaSizeGuide);
  register('nidraka-carousel', NidrakaCarousel);
})();
